import type { CSSProperties, ReactNode } from 'react';
import { ChevronRight, Minus, Plus } from 'lucide-react';

export type Tone = 'blue' | 'cyan' | 'green' | 'yellow' | 'orange' | 'red' | 'purple' | 'muted';

export function Panel({
  children,
  className = '',
  title,
  action,
  style,
}: {
  children: ReactNode;
  className?: string;
  title?: ReactNode;
  action?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <section className={`panel ${className}`} style={style}>
      {(title || action) && (
        <header className="panel__header">
          <div>{title}</div>
          <div>{action}</div>
        </header>
      )}
      {children}
    </section>
  );
}

export function Pill({ children, tone = 'blue' }: { children: ReactNode; tone?: Tone }) {
  return <span className={`pill pill--${tone}`}>{children}</span>;
}

export function Delta({ value, tone }: { value: string; tone?: Tone }) {
  const inferred = tone ?? (value.trim().startsWith('-') ? 'red' : 'green');
  return <span className={`delta delta--${inferred}`}>{value}</span>;
}

export function MiniLineChart({
  values,
  tone = 'blue',
  height = 42,
  area = false,
}: {
  values: number[];
  tone?: Tone;
  height?: number;
  area?: boolean;
}) {
  const width = 180;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(max - min, 1);
  const points = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * width;
      const y = height - 4 - ((value - min) / span) * (height - 10);
      return `${x},${y}`;
    })
    .join(' ');
  const areaPoints = `0,${height} ${points} ${width},${height}`;
  return (
    <svg className={`mini-chart mini-chart--${tone}`} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
      {area && <polygon points={areaPoints} className="mini-chart__area" />}
      <polyline points={points} fill="none" className="mini-chart__line" />
    </svg>
  );
}

export function MetricCard({
  label,
  value,
  unit,
  delta,
  tone = 'blue',
  icon,
  values = [2, 4, 3, 6, 5, 8, 7, 10],
}: {
  label: string;
  value: string;
  unit?: string;
  delta?: string;
  tone?: Tone;
  icon?: ReactNode;
  values?: number[];
}) {
  return (
    <article className="metric-card">
      <div className={`metric-card__icon metric-card__icon--${tone}`}>{icon}</div>
      <div className="metric-card__body">
        <div className="metric-card__label">{label}</div>
        <div className="metric-card__value">
          {value} {unit && <small>{unit}</small>}
        </div>
        {delta && <Delta value={delta} />}
      </div>
      <MiniLineChart values={values} tone={tone} height={34} />
    </article>
  );
}

export function StatusRow({
  icon,
  label,
  value,
  detail,
  tone = 'blue',
}: {
  icon?: ReactNode;
  label: string;
  value: string;
  detail?: string;
  tone?: Tone;
}) {
  return (
    <div className="status-row">
      <span className={`status-row__icon status-row__icon--${tone}`}>{icon}</span>
      <span className="status-row__copy">
        <small>{label}</small>
        <strong>{value}</strong>
      </span>
      {detail && <span className={`status-row__detail status-row__detail--${tone}`}>{detail}</span>}
    </div>
  );
}

export function Donut({
  value,
  label,
  tone = 'green',
  size = 120,
}: {
  value: number;
  label: string;
  tone?: Tone;
  size?: number;
}) {
  const style = { '--donut-value': `${value * 3.6}deg`, '--donut-size': `${size}px` } as CSSProperties;
  return (
    <div className={`donut donut--${tone}`} style={style} role="img" aria-label={`${label}: ${value}%`}>
      <div className="donut__inner">
        <strong>{value}%</strong>
        <small>{label}</small>
      </div>
    </div>
  );
}

export function Segmented({
  items,
  value,
  onChange,
}: {
  items: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="segmented" role="tablist">
      {items.map((item) => (
        <button key={item} type="button" className={value === item ? 'is-active' : ''} onClick={() => onChange(item)}>
          {item}
        </button>
      ))}
    </div>
  );
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (value: boolean) => void; label: string }) {
  return (
    <label className="toggle-row">
      <span>{label}</span>
      <button
        type="button"
        className={`toggle ${checked ? 'is-on' : ''}`}
        aria-pressed={checked}
        onClick={() => onChange(!checked)}
      >
        <span />
      </button>
    </label>
  );
}

export function MapZoomControls({ onZoomIn, onZoomOut }: { onZoomIn: () => void; onZoomOut: () => void }) {
  return (
    <div className="map-zoom-controls">
      <button type="button" aria-label="Zoom in" onClick={onZoomIn}><Plus size={17} /></button>
      <button type="button" aria-label="Zoom out" onClick={onZoomOut}><Minus size={17} /></button>
    </div>
  );
}

export function ListLink({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <button type="button" className="list-link" onClick={onClick}>
      <span>{children}</span><ChevronRight size={15} />
    </button>
  );
}

export function ProgressBar({ value, tone = 'blue' }: { value: number; tone?: Tone }) {
  return (
    <div className="progress-track" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100}>
      <span className={`progress-fill progress-fill--${tone}`} style={{ width: `${value}%` }} />
    </div>
  );
}
